import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import API from '../services/api';
import { useApi } from '../hooks/useApi';

function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { data: pageContent } = useApi(() => API.content.getPageContent('blog'));

  // API Data
  const { data: postsData, loading: postsLoading } = useApi(() => API.blog.getPosts(1, 50));
  const { data: categoriesData, loading: categoriesLoading } = useApi(() => API.blog.getCategories());
  const { data: featuredData } = useApi(() => API.blog.getFeatured(2));

  // Extract data
  const allPosts = postsData?.posts || [];
  const apiCategories = categoriesData || [];
  const featuredPosts = featuredData?.posts || [];

  // ✅ STEP 1: Separate featured and non-featured posts
  const allNonFeaturedPosts = allPosts.filter(post => !post.is_featured);

  // ✅ STEP 2: Build categories with correct counts (only non-featured posts)
  const categories = [
    { id: 'all', name: 'All Posts', count: allNonFeaturedPosts.length },
    ...apiCategories.map(cat => ({
      id: cat.slug,
      name: cat.name,
      count: allNonFeaturedPosts.filter(post => post.category_slug === cat.slug).length
    }))
  ];

  // ✅ STEP 3: Filter non-featured posts by selected category
  const nonFeaturedPosts = selectedCategory === 'all'
    ? allNonFeaturedPosts
    : allNonFeaturedPosts.filter(post => post.category_slug === selectedCategory);



  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden section-header reveal-on-scroll page-top-spacing">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${pageContent?.hero_background?.image_url || "https://images.pexels.com/photos/2403205/pexels-photo-2403205.jpeg"}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/40"></div>
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Abstract geometric pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0 bg-repeat mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M50 50L25 25h50L50 50zM50 50l25 25H25l25-25z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          ></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <div className="section-header">
            <div className="section-overline text-gold mb-4 font-semibold tracking-wide text-sm uppercase">
              Stories & Guides
            </div>
            <h1 className="section-title font-playfair text-4xl md:text-6xl font-bold mb-6 text-white">
             {pageContent?.hero_title?.content_value || 'Adventure Blog'}
            </h1>
          </div>
          <p className="font-graphik text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
            {pageContent?.hero_description?.content_value || 'Discover insider tips, adventure stories, and everything you need to know about ATV touring in St. Vincent.'}
          </p>
        </div>
      </section>

      {/* Featured Posts Section */}
      <section className="py-16  bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="section-header text-center mb-12">
            <div className="section-overline text-gold mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Featured
              </span>
            </div>
            <h2 className="section-title font-playfair text-3xl md:text-4xl font-bold text-emerald-800 dark:text-white mb-6">Latest Stories</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {postsLoading ? (
              <div className="col-span-2 text-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : featuredPosts.length > 0 ? (
              featuredPosts.slice(0, 2).map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id}>
                  <article className="bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-xl border border-emerald-100 dark:border-gray-700 group hover:shadow-2xl transition-all duration-300">
                    <div className="relative overflow-hidden h-64">
                      <img
                        src={post.image_url || '/pics/default-blog.jpg'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-graphik font-medium uppercase tracking-wide">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-8">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                        <span className="font-graphik">{post.author_name}</span>
                        <span>•</span>
                        <span className="font-graphik">{post.published_date}</span>
                        <span>•</span>
                        <span className="font-graphik">{post.reading_time}</span>
                      </div>

                      <h3 className="font-playfair text-xl md:text-2xl font-bold text-emerald-800 dark:text-white mb-3 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300">
                        {post.title}
                      </h3>

                      <p className="font-graphik text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                        {post.excerpt}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.map((tag, index) => (
                          <span key={index} className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-lg text-xs font-graphik">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <button className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-graphik font-medium flex items-center space-x-2 group-hover:space-x-3 transition-all duration-300">
                        <span>Read More</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </article>
                </Link>
              ))
            ) : (
              <div className="col-span-2 text-center py-20">
                <p className="text-gray-600 dark:text-gray-400">No featured posts available</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Category Filter & All Posts */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-6">
          <div className="section-header text-center mb-12">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Browse
              </span>
            </div>
            <h2 className="section-title font-playfair text-3xl md:text-4xl font-bold text-emerald-800 dark:text-white mb-6">All Posts</h2>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-graphik font-medium transition-all duration-300 flex items-center space-x-2 ${selectedCategory === category.id
                  ? 'bg-emerald-600 text-white shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-700 dark:hover:text-emerald-400 border border-gray-200 dark:border-gray-700'
                  }`}
              >
                <span>{category.name}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${selectedCategory === category.id
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                  {category.count}
                </span>
              </button>
            ))}
          </div>

          {/* Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {postsLoading ? (
              <div className="col-span-3 text-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : nonFeaturedPosts.length > 0 ? (
              nonFeaturedPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id}>
                  <article className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg border border-gray-100 dark:border-gray-700 group hover:shadow-xl hover:border-emerald-200 dark:hover:border-emerald-600 transition-all duration-300">
                    <div className="relative overflow-hidden h-48">
                      <img
                        src={post.image_url || '/pics/default-blog.jpg'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="bg-emerald-600 text-white px-2 py-1 rounded-lg text-xs font-graphik font-medium uppercase tracking-wide">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400 mb-3">
                        <span className="font-graphik">{post.author_name}</span>
                        <span>•</span>
                        <span className="font-graphik">{post.published_date}</span>
                      </div>

                      <h3 className="font-playfair text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors duration-300 line-clamp-2">
                        {post.title}
                      </h3>

                      <p className="font-graphik text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="font-graphik text-xs text-gray-500 dark:text-gray-400">{post.reading_time}</span>
                        <button className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-graphik text-sm font-medium flex items-center space-x-1 group-hover:space-x-2 transition-all duration-300">
                          <span>Read</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </article>
                </Link>
              ))
            ) : (
              <div className="col-span-3 text-center py-20">
                <p className="text-gray-600 dark:text-gray-400">No posts found</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-emerald-800 reveal-on-scroll">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="section-header mb-8">
              <div className="section-overline mb-4">
                <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                  Stay Updated
                </span>
              </div>
              <h2 className="section-title font-playfair text-3xl md:text-4xl font-bold text-white mb-4">
                Adventure Newsletter
              </h2>
              <p className="font-graphik text-xl text-white/90 max-w-2xl mx-auto">
                Get the latest adventure stories, travel tips, and exclusive tour offers delivered to your inbox.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto mb-8">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-xl border-0 focus:ring-2 focus:ring-white/50 font-graphik"
              />
              <button className="btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </div>

            <p className="font-graphik text-sm text-white/70">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-white dark:bg-gray-900 reveal-on-scroll">
        <div className="container mx-auto px-6">
          <div className="section-header text-center mb-8">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Ready for Adventure?
              </span>
            </div>
            <h2 className="section-title font-playfair text-3xl font-bold text-emerald-800 dark:text-white">Book Your Tour Today</h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            <Link
              to="/tours"
              className="btn-primary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Book Adventure</span>
            </Link>

            <Link
              to="/contact"
              className="btn-secondary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogPage;
